import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, ExternalLink, Link2 } from "lucide-react";
import { B, fig, ddmon, Pill } from "../clients/record/BillingCard";
import { MrrPanel } from "./MrrPanel";
import { SeatsPanel } from "./SeatsPanel";
import { UnresolvedPanel } from "./UnresolvedPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/billing`;
const MANROPE = { fontFamily: "Manrope, sans-serif" };
const FOCUS = "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFD108]";
const clientHref = (id) => `/clients/${encodeURIComponent(id || "")}`;

const Tile = ({ label, value, tone, onClick, active, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`text-left px-3.5 py-2.5 rounded-[9px] transition-colors ${FOCUS} hover:bg-[#FBFAF7]`}
    style={{ background: B.card, border: `1px solid ${active ? B.accentEdge : B.border}` }}>
    <div className="text-[10px] font-bold uppercase tracking-[0.09em]" style={{ color: B.ink3 }}>{label}</div>
    <div className="mono text-[17px] font-bold" style={{ color: tone || B.ink }}>{value}</div>
  </button>
);

const Head = ({ children }) => (
  <div className="text-[10px] font-bold uppercase tracking-[0.09em] mb-1.5" style={{ color: B.ink3 }}>{children}</div>
);

export default function BillingOverview() {
  const [d, setD] = useState(null);
  const [list, setList] = useState("overdue");
  const [match, setMatch] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/overview`).then((r) => setD(r.data))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Could not load billing"); setD({}); });
    axios.get(`${API}/unmatched`).then((r) => setMatch(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try { await axios.post(`${API}/sync`); toast.success("Read back from Stripe"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Stripe sync failed"); }
    finally { setSyncing(false); }
  };

  const link = async (customerId, clientId) => {
    try {
      await axios.post(`${API}/match`, { customer_id: customerId, client_id: clientId || null });
      toast.success(clientId ? "Linked" : "Left unmatched");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not link that customer"); }
  };

  if (!d) return <div className="text-[13px]" style={{ color: B.ink2 }}>Loading…</div>;
  const c = d.counts || {};
  const rows = { overdue: d.overdue || [], failed: d.failed || [], cancelling: d.cancelling || [],
    no_billing: d.no_billing || [], needs_match: match?.customers || [] }[list] || [];

  return (
    <div className="space-y-3.5" data-testid="billing-overview">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 style={{ ...MANROPE, fontSize: 25, fontWeight: 800, color: B.ink, letterSpacing: "-0.018em" }}>
            Billing
          </h1>
          <p className="text-[13px]" style={{ color: B.ink2 }}>
            What the company actually earns, and what Stripe is collecting against it.
            {d.last_sync_at ? ` Stripe read ${new Date(d.last_sync_at).toLocaleString("en-AU")}.` : ""}
          </p>
        </div>
        <button onClick={sync} disabled={syncing} data-testid="billing-overview-sync"
          className={`h-[34px] px-3.5 rounded-[7px] inline-flex items-center gap-1.5 text-[12px] font-bold uppercase ${FOCUS}`}
          style={{ background: B.accent, border: `1px solid ${B.accentEdge}`, color: "#000" }}>
          <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> Read from Stripe
        </button>
      </div>

      <MrrPanel />
      <SeatsPanel />
      <UnresolvedPanel />

      <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
        {(d.mrr || []).map((m) => (
          <Tile key={m.currency} label={`Stripe recurring (${m.currency})`} value={fig(m)}
            testid={`billing-mrr-${m.currency}`} />
        ))}
        {(d.paid_90d || []).map((m) => (
          <Tile key={m.currency} label={`Collected 90d (${m.currency})`} value={fig(m)}
            testid={`billing-paid90-${m.currency}`} />
        ))}
        {(d.dockets_90d || []).map((m) => (
          <Tile key={m.currency} label={`Dockets 90d (${m.currency})`} value={fig(m)}
            testid={`billing-dockets-${m.currency}`} />
        ))}
        <Tile label="Overdue" value={c.overdue ?? 0} tone={c.overdue ? B.bad : B.ink}
          active={list === "overdue"} onClick={() => setList("overdue")} testid="billing-tile-overdue" />
        <Tile label="Failed payments" value={c.failed ?? 0} tone={c.failed ? B.bad : B.ink}
          active={list === "failed"} onClick={() => setList("failed")} testid="billing-tile-failed" />
        <Tile label="Cancelling" value={c.cancelling ?? 0} tone={B.accentInk}
          active={list === "cancelling"} onClick={() => setList("cancelling")} testid="billing-tile-cancelling" />
        <Tile label="No billing set up" value={c.no_billing ?? 0} active={list === "no_billing"}
          onClick={() => setList("no_billing")} testid="billing-tile-nobilling" />
        <Tile label="Needs matching" value={c.needs_match ?? 0} tone={c.needs_match ? B.accentInk : B.ink}
          active={list === "needs_match"} onClick={() => setList("needs_match")} testid="billing-tile-match" />
      </div>

      {(d.trialing || []).length > 0 && (
        <div className="text-[12.5px]" style={{ color: B.ink2 }} data-testid="billing-trialing">
          Not in that figure: {(d.trialing || []).map((m) => fig(m)).join(" · ")} committed on trials
          that have not started paying yet.
        </div>
      )}

      {Object.keys(d.overdue_totals || {}).length > 0 && (
        <div className="text-[12.5px]" style={{ color: B.ink2 }} data-testid="billing-overdue-totals">
          Overdue totals: {Object.entries(d.overdue_totals).map(([cur, amt]) =>
            fig({ currency: cur, amount: amt })).join(" · ")}
        </div>
      )}

      <div className="rounded-[11px] overflow-hidden" style={{ background: B.card, border: `1px solid ${B.border}` }}>
        <div className="px-3.5 py-2.5" style={{ background: B.panel, borderBottom: `1px solid ${B.border}` }}>
          <Head>{{ overdue: "Overdue invoices", failed: "Failed payments", cancelling: "Cancelling",
            no_billing: "Clients with no billing set up",
            needs_match: "Stripe customers needing a client" }[list]}</Head>
          {list === "needs_match" && (
            <p className="text-[11.5px]" style={{ color: B.ink3 }}>
              Pick the client once and the link sticks. Anything left unmatched still counts as revenue here.
            </p>
          )}
        </div>
        {rows.length === 0 ? (
          <div className="px-3.5 py-8 text-[13px]" style={{ color: B.ink2 }} data-testid="billing-list-empty">
            Nothing in this list.
          </div>
        ) : rows.map((r, i) => (
          <div key={r.invoice_id || r.subscription_id || r.id || r.client_id || i}
            className="px-3.5 py-2.5 grid gap-3 items-center text-[12.5px]"
            style={{ gridTemplateColumns: list === "needs_match" ? "minmax(0,1fr) 150px 210px"
              : "minmax(0,1.2fr) 110px 100px 110px", borderBottom: `1px solid ${B.divider}` }}
            data-testid={`billing-row-${r.invoice_id || r.id || r.client_id || i}`}>
            {list === "needs_match" ? (
              <>
                <span className="truncate" style={{ color: B.ink }}>
                  {r.name || "(no name)"} <span style={{ color: B.ink3 }}>{r.email || ""}</span>
                </span>
                <span className="mono" style={{ color: B.ink2 }}>{fig(r.paid_total)} paid</span>
                <span className="flex items-center gap-1.5">
                  <select defaultValue="" data-testid={`billing-match-select-${r.id}`}
                    onChange={(e) => e.target.value && link(r.id, e.target.value)}
                    className={`h-8 flex-1 rounded-[7px] px-1.5 text-[12px] bg-white ${FOCUS}`}
                    style={{ border: `1px solid ${B.border}`, color: B.ink }}>
                    <option value="">Pick the client…</option>
                    {(match?.clients || []).map((cl) => (
                      <option key={cl.client_id} value={cl.client_id}>{cl.company_name}</option>
                    ))}
                  </select>
                  <a href={r.dashboard_url} target="_blank" rel="noreferrer" title="Open in Stripe">
                    <ExternalLink className="w-3.5 h-3.5" style={{ color: B.ink3 }} />
                  </a>
                </span>
              </>
            ) : (
              <>
                <a href={clientHref(r.client_id)} className={`truncate ${FOCUS}`} style={{ color: B.ink }}
                  data-testid={`billing-client-link-${r.client_id || i}`}>
                  {r.company_name || r.client_id || "Unmatched customer"}
                  {r.number ? <span style={{ color: B.ink3 }}> · {r.number}</span> : null}
                  {r.products?.length ? <span style={{ color: B.ink3 }}> · {r.products.join(", ")}</span> : null}
                </a>
                <span className="mono text-right" style={{ color: B.ink }}>{r.money ? fig(r.money) : "—"}</span>
                <span className="mono" style={{ color: r.days_overdue ? B.bad : B.ink2 }}>
                  {list === "overdue" ? `${r.days_overdue}d late`
                    : list === "failed" ? `${r.attempts || 1} attempt${(r.attempts || 1) > 1 ? "s" : ""}`
                    : list === "cancelling" ? ddmon(r.ends) : ""}
                </span>
                <span className="flex items-center justify-end gap-1.5">
                  {r.hosted_invoice_url && (
                    <a href={r.hosted_invoice_url} target="_blank" rel="noreferrer" title="Open the invoice">
                      <ExternalLink className="w-3.5 h-3.5" style={{ color: B.ink3 }} />
                    </a>
                  )}
                  {list === "no_billing" && <Pill value="not set up" loud />}
                  {list === "failed" && <Pill value="failed" loud />}
                  {list === "overdue" && <Pill value="open" />}
                  {list === "cancelling" && <Link2 className="w-3.5 h-3.5" style={{ color: B.ink3 }} />}
                </span>
              </>
            )}
          </div>
        ))}
      </div>

      {(d.mrr || []).some((m) => m.products?.length) && (
        <div className="rounded-[11px] p-3.5" style={{ background: B.card, border: `1px solid ${B.border}` }}
          data-testid="billing-mrr-breakdown">
          <Head>Monthly recurring by product</Head>
          {(d.mrr || []).map((m) => (
            <div key={m.currency} className="mb-2">
              <div className="text-[11.5px] mb-1" style={{ color: B.ink3 }}>{m.currency}</div>
              {m.products.map((p) => (
                <div key={p.product} className="flex items-baseline gap-2 text-[12.5px] py-0.5"
                  style={{ borderBottom: `1px solid ${B.divider}` }}>
                  <span style={{ color: B.ink }}>{p.product}</span>
                  <span className="mono ml-auto" style={{ color: B.ink2 }}>{fig(p)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {(d.subscription_lines || []).length > 0 && (
        <div className="rounded-[11px] overflow-hidden" style={{ background: B.card, border: `1px solid ${B.border}` }}
          data-testid="billing-mrr-audit">
          <div className="px-3.5 py-2.5" style={{ background: B.panel, borderBottom: `1px solid ${B.border}` }}>
            <Head>Who is paying, for what</Head>
            <p className="text-[11.5px]" style={{ color: B.ink3 }}>
              Every live subscription behind the figure above. A plan billed yearly or quarterly counts
              as its monthly share, not the whole invoice.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]" style={{ borderCollapse: "collapse", minWidth: 860 }}>
              <thead>
                <tr style={{ color: B.ink3, background: B.panel }}>
                  {[["Account", "left"], ["Products", "left"], ["Stripe bills", "right"],
                    ["Every", "left"], ["A month", "right"], ["Renews", "left"],
                    ["Pays by", "left"], ["", "left"]].map(([h, a]) => (
                    <th key={h} className="text-[10px] font-bold uppercase tracking-[0.09em] px-3 py-1.5 whitespace-nowrap"
                      style={{ borderBottom: `1px solid ${B.border}`, textAlign: a }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(d.subscription_lines || []).map((l) => (
                  <tr key={l.subscription_id} data-testid={`billing-mrr-row-${l.subscription_id}`}>
                    <td className="px-3 py-2" style={{ borderBottom: `1px solid ${B.divider}` }}>
                      <a href={clientHref(l.client_id)} className={FOCUS} style={{ color: B.ink, fontWeight: 600 }}>
                        {l.company_name}
                      </a>
                      {l.status === "trialing" && (
                        <span className="ml-1.5 text-[10px] font-bold uppercase" style={{ color: B.accentInk }}>trial</span>
                      )}
                      {l.cancel_at_period_end && (
                        <span className="ml-1.5 text-[10px] font-bold uppercase" style={{ color: B.bad }}>cancelling</span>
                      )}
                      {(l.customer_email || l.customer_name) && (
                        <div className="text-[11px] truncate" style={{ color: B.ink3, maxWidth: 240 }}>
                          {l.customer_email || l.customer_name}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2" style={{ borderBottom: `1px solid ${B.divider}` }}>
                      {(l.product_lines || []).length ? l.product_lines.map((p) => (
                        <div key={p.product} className="flex items-baseline gap-2 whitespace-nowrap">
                          <span style={{ color: B.ink2 }}>{p.product}</span>
                          <span className="mono text-[11.5px]" style={{ color: B.ink3 }}>{fig(p)}/mo</span>
                        </div>
                      )) : <span style={{ color: B.ink3 }}>{(l.products || []).join(" + ") || "—"}</span>}
                    </td>
                    <td className="px-3 py-2 mono whitespace-nowrap"
                      style={{ borderBottom: `1px solid ${B.divider}`, color: B.ink2, textAlign: "right" }}>{fig(l.billed)}</td>
                    <td className="px-3 py-2 whitespace-nowrap"
                      style={{ borderBottom: `1px solid ${B.divider}`, color: B.ink3 }}>{l.every || "—"}</td>
                    <td className="px-3 py-2 mono whitespace-nowrap"
                      style={{ borderBottom: `1px solid ${B.divider}`, color: B.ink, fontWeight: 700, textAlign: "right" }}>{fig(l.monthly)}</td>
                    <td className="px-3 py-2 whitespace-nowrap"
                      style={{ borderBottom: `1px solid ${B.divider}`, color: B.ink3 }}>{ddmon(l.renews) || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap"
                      style={{ borderBottom: `1px solid ${B.divider}`, color: B.ink3 }}>
                      {l.card || (l.collection_method === "send_invoice" ? "Invoice" : "—")}
                    </td>
                    <td className="px-3 py-2" style={{ borderBottom: `1px solid ${B.divider}` }}>
                      {l.dashboard_url && (
                        <a href={l.dashboard_url} target="_blank" rel="noreferrer" title="Open in Stripe"
                          data-testid={`billing-mrr-open-${l.subscription_id}`}>
                          <ExternalLink className="w-3.5 h-3.5" style={{ color: B.ink3 }} />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(d.recent_payments || []).length > 0 && (
        <div className="rounded-[11px] overflow-hidden" style={{ background: B.card, border: `1px solid ${B.border}` }}
          data-testid="billing-recent-payments">
          <div className="px-3.5 py-2.5" style={{ background: B.panel, borderBottom: `1px solid ${B.border}` }}>
            <Head>Who has paid — last 90 days</Head>
            <p className="text-[11.5px]" style={{ color: B.ink3 }}>
              Settled invoices, newest first. {(d.paid_90d || []).map((m) => fig(m)).join(" · ")} collected.
            </p>
          </div>
          {(d.recent_payments || []).map((p) => (
            <div key={p.invoice_id} data-testid={`billing-paid-${p.invoice_id}`}
              className="px-3.5 py-2 grid gap-3 items-center text-[12.5px]"
              style={{ gridTemplateColumns: "minmax(0,1.4fr) minmax(0,1fr) 92px 100px 24px",
                       borderBottom: `1px solid ${B.divider}` }}>
              <a href={clientHref(p.client_id)} className={`truncate ${FOCUS}`}
                style={{ color: B.ink, fontWeight: 600 }}>{p.company_name}</a>
              <span className="truncate" style={{ color: B.ink3 }}>
                {(p.products || []).filter(Boolean).join(", ") || p.number || "Invoice"}
                {p.is_docket && <span style={{ color: B.accentInk }}> · docket</span>}
              </span>
              <span className="mono text-right" style={{ color: B.ink }}>{fig(p.money)}</span>
              <span style={{ color: B.ink3 }}>{ddmon(p.paid_at)}</span>
              <span>
                {p.hosted_invoice_url && (
                  <a href={p.hosted_invoice_url} target="_blank" rel="noreferrer" title="Open the invoice">
                    <ExternalLink className="w-3.5 h-3.5" style={{ color: B.ink3 }} />
                  </a>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
